import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCarSide } from "react-icons/fa";
import { fleetService, vehicleImageUrl } from "../services/fleetService";
import { useI18n } from "../providers/I18nProvider";
import { FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";
import "../styles/Fleet.css";

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    let cancelled = false;
    fleetService
      .listVehicles()
      .then((data) => {
        if (!cancelled) setVehicles(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err.response?.data?.msg || t("fleet.loadError"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const list = vehicles.filter(
      (v) => !q || `${v.name || ""} ${v.plate || ""}`.toLocaleLowerCase("tr-TR").includes(q)
    );
    const key = sortKey === "plate" ? "plate" : "name";
    return [...list].sort((a, b) =>
      String(a[key] || "").localeCompare(String(b[key] || ""), "tr")
    );
  }, [vehicles, search, sortKey]);

  // Layout role'e gore RoleAwareLayout tarafindan seciliyor; detay sayfasi
  // da ayni sekilde calistigi icin tek bir /vehicles/:id path'i yeterli.
  const go = (id) => {
    navigate(`/vehicles/${id}`);
  };

  return (
    <div className="fleet-page">
      <div className="fleet-page-header">
        <div>
          <h2>{t("fleet.vehiclesTitle")}</h2>
          <div className="subtitle">{t("fleet.vehiclesSubtitle")}</div>
        </div>
      </div>

      {loading && <div className="fleet-empty">{t("common.loading")}</div>}
      {error && (
        <div className="fleet-error" role="alert">
          {error}
        </div>
      )}
      {!loading && !error && vehicles.length > 1 && (
        <FilterBar showReset={Boolean(search)} onReset={() => setSearch("")}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("fleetUi.filters.searchVehicle")}
          />
          <select
            className="leaves-filter-input lt-input"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label={t("fleetUi.filters.sort")}
          >
            <option value="name">{t("fleetUi.filters.sortNameAsc")}</option>
            <option value="plate">{t("fleetUi.filters.sortPlateAsc")}</option>
          </select>
        </FilterBar>
      )}
      {!loading && !error && vehicles.length === 0 && (
        <EmptyState message={t("fleet.noVehicles")} />
      )}
      {!loading && !error && vehicles.length > 0 && visible.length === 0 && (
        <EmptyState message={t("fleetUi.emptyVehiclesFiltered")} />
      )}

      <div className="vehicle-grid">
        {visible.map((v) => {
          const imgUrl = vehicleImageUrl(v.image_path);
          return (
            <div
              key={v.id}
              className="vehicle-card"
              onClick={() => go(v.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  go(v.id);
                }
              }}
            >
              <div
                className="vehicle-card-image"
                style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
              >
                {!imgUrl && (
                  <div className="vehicle-card-placeholder">
                    <FaCarSide />
                  </div>
                )}
              </div>
              <div className="vehicle-card-body">
                <h3 className="vehicle-card-title">{v.name}</h3>
                {v.plate && <div className="vehicle-card-plate">{v.plate}</div>}
                <span
                  className={`vehicle-card-status ${
                    v.is_active ? "" : "inactive"
                  }`}
                >
                  {v.is_active ? t("fleet.active") : t("fleet.inactive")}
                </span>
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="fleet-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      go(v.id);
                    }}
                  >
                    {t("fleet.viewCalendar")}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehiclesPage;
